// Armar la grilla de un mes.
//
// POR QUÉ ESTO NO VIVE ADENTRO DEL COMPONENTE
//
// Un calendario parece trivial hasta que se escribe. Los errores que
// tiene son todos silenciosos: el mes arranca corrido un día, la última
// semana pierde el 31, el domingo 1 genera una fila vacía arriba. Nada
// de eso tira un error — simplemente muestra mal, y nadie lo nota
// porque un calendario mal armado sigue pareciendo un calendario.
//
// Separado y en funciones puras, se puede probar con fechas conocidas.
// Ahí sí un mes corrido falla un test en vez de pasar desapercibido.
//
// TODO ACÁ ES HORA LOCAL, A PROPÓSITO
//
// Es la misma lección que dejó el bug de "Hoy vs Ayer" (ver
// src/fechas.js): la app registra la fecha con el mediodía local fijado
// justamente para que no se corra de día. Si el calendario agrupara en
// UTC, un outfit registrado un lunes a las 21:00 en Paraguay caería en
// martes y el usuario vería un día que no vivió.

/* LOS NOMBRES DE DÍAS Y MESES LOS DA EL NAVEGADOR.
 *
 * Antes eran dos arrays en español escritos a mano. Con doce idiomas
 * eso serían 12 × 19 = 228 palabras traducidas a mano, y ninguna
 * traducción habría acertado sola cosas como que el japonés escribe los
 * meses "1月, 2月" o que el árabe abrevia los días con la palabra
 * entera. `Intl` las sabe todas.
 *
 * ⚠️  Y NO CAMBIA NADA EN ESPAÑOL, que es lo que permitió hacerlo sin
 *     riesgo: `Intl` devuelve exactamente `lun mar mié jue vie sáb dom`
 *     y `enero febrero…`, idénticos a los arrays que reemplaza. Se
 *     verificó antes de escribir esto, no después.
 *
 * ⚠️  EL IDIOMA ENTRA POR PARÁMETRO. Este módulo lo carga su test con
 *     `require()` de node, donde `src/i18n.js` —que importa React— no se
 *     puede resolver. Además es un módulo de funciones puras a
 *     propósito: pedirle el idioma a un singleton lo volvería
 *     imposible de probar con doce idiomas.
 *
 * `timeZone: "UTC"` no es opcional: sin eso el formateador aplica la
 * zona local y, para alguien al oeste de Greenwich, el lunes 1 de enero
 * se imprimiría como domingo. Es el mismo error de un día que este
 * archivo persigue en todo lo demás.
 *
 * Se memoriza por idioma: `armarMes` se llama en cada repintado del
 * calendario y construir dos formateadores cada vez es caro. */
const CACHE_DIAS = new Map();
const CACHE_MESES = new Map();

// Lunes primero. El 1 de enero de 2024 fue lunes, así que sirve de
// ancla para sacar los siete nombres en orden sin depender de la fecha
// de hoy ni de qué día empieza la semana en cada país.
function nombresDeDia(idioma = "es") {
  if (!CACHE_DIAS.has(idioma)) {
    const f = new Intl.DateTimeFormat(idioma, { weekday: "short", timeZone: "UTC" });
    CACHE_DIAS.set(
      idioma,
      Array.from({ length: 7 }, (_, i) => f.format(new Date(Date.UTC(2024, 0, 1 + i))))
    );
  }
  return CACHE_DIAS.get(idioma);
}

function nombresDeMes(idioma = "es") {
  if (!CACHE_MESES.has(idioma)) {
    const f = new Intl.DateTimeFormat(idioma, { month: "long", timeZone: "UTC" });
    // Día 15: cualquier día del medio evita los bordes de mes.
    CACHE_MESES.set(
      idioma,
      Array.from({ length: 12 }, (_, i) => f.format(new Date(Date.UTC(2024, i, 15))))
    );
  }
  return CACHE_MESES.get(idioma);
}

function claveDia(fecha) {
  const d = fecha instanceof Date ? fecha : new Date(fecha);
  if (Number.isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/**
 * Los registros indexados por día local.
 *
 * Un día puede tener MÁS DE UN outfit: te cambiaste para salir a la
 * noche. Por eso el valor es una lista y no un registro suelto — el
 * primer diseño guardaba uno y perdía el segundo en silencio.
 */
function porDia(registros) {
  const mapa = new Map();
  for (const o of registros || []) {
    const clave = claveDia(o?.usada_en);
    if (!clave) continue;
    if (!mapa.has(clave)) mapa.set(clave, []);
    mapa.get(clave).push(o);
  }
  return mapa;
}

/**
 * Las semanas de un mes, como filas de 7 celdas.
 *
 * Cada celda es `{ clave, dia, delMes, esHoy, outfits }`. Las celdas de
 * relleno —los días del mes anterior y del siguiente que completan la
 * primera y la última semana— vienen con `delMes: false` y se dibujan
 * apagadas. Se devuelven en vez de omitirse porque una grilla de 7
 * columnas necesita las 7 celdas o se desalinea.
 *
 * @param anio  año completo (2026)
 * @param mes   0-11, como `Date.getMonth()`
 */
function armarMes(registros, anio, mes, hoy = new Date()) {
  const indice = porDia(registros);
  const claveHoy = claveDia(hoy);

  const primero = new Date(anio, mes, 1);
  // getDay() da 0 para domingo. Con la semana empezando en lunes, el
  // domingo es la séptima columna, no la primera.
  const desplazamiento = (primero.getDay() + 6) % 7;

  const celdas = [];
  // Arranca en el lunes de la semana que contiene al día 1, aunque ese
  // lunes sea del mes anterior. `new Date` con día 0 o negativo retrocede
  // solo — no hace falta calcular cuántos días tenía el mes pasado.
  const inicio = new Date(anio, mes, 1 - desplazamiento);

  // Siempre 6 semanas. Un mes ocupa 5 o 6 según dónde caiga el día 1, y
  // si la grilla cambia de alto entre meses, la página salta al navegar.
  // Seis filas fijas cuestan una fila apagada de más y a cambio el mes
  // siguiente aparece donde estaba el anterior.
  for (let i = 0; i < 42; i++) {
    const d = new Date(inicio.getFullYear(), inicio.getMonth(), inicio.getDate() + i);
    const clave = claveDia(d);
    celdas.push({
      clave,
      dia: d.getDate(),
      delMes: d.getMonth() === mes && d.getFullYear() === anio,
      esHoy: clave === claveHoy,
      outfits: indice.get(clave) || [],
    });
  }

  const semanas = [];
  for (let i = 0; i < 42; i += 7) semanas.push(celdas.slice(i, i + 7));
  return semanas;
}

/**
 * Los meses que tienen al menos un registro, del más nuevo al más viejo.
 *
 * Sirve para no dejar navegar a un enero de 2019 vacío. Devuelve
 * `{ anio, mes, etiqueta, cantidad }`.
 */
function mesesConDatos(registros, idioma = "es") {
  const mapa = new Map();
  for (const o of registros || []) {
    const d = new Date(o?.usada_en);
    if (Number.isNaN(d.getTime())) continue;
    const clave = `${d.getFullYear()}-${d.getMonth()}`;
    const previo = mapa.get(clave);
    if (previo) previo.cantidad++;
    else
      mapa.set(clave, {
        anio: d.getFullYear(),
        mes: d.getMonth(),
        etiqueta: `${nombresDeMes(idioma)[d.getMonth()]} ${d.getFullYear()}`,
        cantidad: 1,
      });
  }
  return [...mapa.values()].sort((a, b) => b.anio - a.anio || b.mes - a.mes);
}

/**
 * El resumen del mes: lo que la lista no puede mostrar.
 *
 * `registrados` cuenta DÍAS con registro, no outfits — dos outfits el
 * mismo sábado son un día vestido, no dos. `hasta` corta en hoy cuando
 * el mes es el actual: decir "registraste 12 de 31 días" un día 12 sería
 * un reproche por días que todavía no pasaron.
 */
function resumenDelMes(semanas, hoy = new Date()) {
  const claveHoy = claveDia(hoy);
  let delMes = 0;
  let registrados = 0;
  let transcurridos = 0;
  let alcanzoHoy = false;

  for (const semana of semanas) {
    for (const celda of semana) {
      if (!celda.delMes) continue;
      delMes++;
      if (celda.outfits.length > 0) registrados++;
      // Los días futuros no cuentan como "sin registrar".
      if (!alcanzoHoy) transcurridos++;
      if (celda.clave === claveHoy) alcanzoHoy = true;
    }
  }

  // Si hoy no cae en este mes, el mes está entero en el pasado (o
  // entero en el futuro, y ahí transcurridos queda en su total y el
  // porcentaje da 0, que es correcto).
  const base = alcanzoHoy ? transcurridos : delMes;

  return {
    dias: delMes,
    registrados,
    transcurridos: base,
    sinRegistrar: Math.max(0, base - registrados),
  };
}

module.exports = {
  nombresDeDia,
  nombresDeMes,
  claveDia,
  porDia,
  armarMes,
  mesesConDatos,
  resumenDelMes,
};
